'use client';

import * as React from 'react';
import { Box, Button } from '@mui/material';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ArrowCounterClockwise as ArrowCounterClockwiseIcon } from '@phosphor-icons/react/dist/ssr/ArrowCounterClockwise';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';

import type { Settings } from '@/types/settings';
import type { Mode } from '@/styles/theme/types';

import { OptionsColorScheme } from './options-color-scheme';
import { OptionsDirection } from './options-direction';
import { OptionsLayout } from './options-layout';
import { OptionsNavColor } from './options-nav-color';
import { OptionsPrimaryColor } from './options-primary-color';

export interface SettingsDrawerProps {
  canReset?: boolean;
  onClose?: () => void;
  onReset?: () => void;
  onUpdate?: (settings: Partial<Settings>) => void;
  open?: boolean;
  values?: Partial<Settings> & { mode?: Mode };
}

export function SettingsDrawer({
  canReset = true,
  onClose,
  onUpdate,
  onReset,
  open,
  values = {},
}: SettingsDrawerProps): React.JSX.Element {
  const handleChange = React.useCallback(
    (field: keyof Settings | 'mode', value: unknown) => {
      onUpdate?.({ [field]: value });
    },
    [onUpdate]
  );

  return (
    <Stack>
      <Stack direction="row" spacing={3} sx={{ width: 1, alignItems: 'center', justifyContent: 'end' }}>
        <Box>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Badge
              anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
              color="error"
              sx={{ '& .MuiBadge-badge': { top: 6, right: 6, ...(!canReset && { display: 'none' }) } }}
              variant="dot"
            >
              <Button onClick={onReset} variant="text" endIcon={<ArrowCounterClockwiseIcon />}>
                <Typography variant="button">Reset</Typography>
              </Button>
            </Badge>
          </Stack>
        </Box>
      </Stack>
      <Stack spacing={5} sx={{ overflowY: 'auto', p: 1 }}>
        <OptionsPrimaryColor
          onChange={(value) => {
            handleChange('primaryColor', value);
          }}
          value={values.primaryColor}
        />
        <OptionsColorScheme
          onChange={(value) => {
            handleChange('mode', value);
          }}
          value={values.mode}
        />
        <OptionsNavColor
          onChange={(value) => {
            handleChange('navColor', value);
          }}
          value={values.navColor}
        />
        <OptionsLayout
          onChange={(value) => {
            handleChange('layout', value);
          }}
          value={values.layout}
        />
        {/* <OptionsDirection
          onChange={(value) => {
            handleChange('direction', value);
          }}
          value={values.direction}
        /> */}
      </Stack>
    </Stack>
  );
}
